import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import {
  Search,
  Filter,
  Eye,
  Trash2,
  Send,
  Plus,
  Copy,
  FileText,
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '../../lib/auth';
import { GET_QUOTES } from '../../graphql/queries/quote';
import { GET_AGENCY_BY_NAME } from '../../graphql/admin/queries/client';
import {
  DELETE_QUOTE,
  DUPLICATE_QUOTE,
} from '../../graphql/mutations/quote';

type QuoteStatus = 'DRAFT' | 'SENT' | 'ACCEPTED' | 'REJECTED' | 'EXPIRED' | 'CONVERTED';

const statusColors: Record<QuoteStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-800',
  SENT: 'bg-blue-100 text-blue-800',
  ACCEPTED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-800',
  EXPIRED: 'bg-yellow-100 text-yellow-800',
  CONVERTED: 'bg-purple-100 text-purple-800',
};

const statusLabels: Record<QuoteStatus, string> = {
  DRAFT: 'Brouillon',
  SENT: 'Envoyé',
  ACCEPTED: 'Accepté',
  REJECTED: 'Refusé',
  EXPIRED: 'Expiré',
  CONVERTED: 'Converti',
};

function formatCurrency(amount: number, currency = 'DZD') {
  try {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount.toLocaleString('fr-FR')} ${currency}`;
  }
}

function formatDate(value?: string | null) {
  if (!value) return '—';
  try {
    return format(new Date(value), 'dd MMM yyyy', { locale: fr });
  } catch {
    return value;
  }
}

interface QuoteRow {
  id: number;
  reference: string;
  status: QuoteStatus;
  source: 'AGENT' | 'CLIENT';
  title: string;
  destination?: string | null;
  contactNom: string;
  contactPrenom: string;
  contactEmail: string;
  totalPrice: number;
  currency: string;
  validUntil: string;
  travelDateStart?: string | null;
  createdAt: string;
}

export default function QuotesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userAgencyId = (user as any)?.agencyId || (user as any)?.agency?.id;
  const agencyName = (user as any)?.agencyName || (user as any)?.agency?.name;

  const { data: agencyLookup } = useQuery(GET_AGENCY_BY_NAME, {
    variables: { name: agencyName },
    skip: Boolean(userAgencyId) || !agencyName,
  });
  const agencyId = userAgencyId || agencyLookup?.agency?.id;

  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | QuoteStatus>('all');
  const [tab, setTab] = useState<'all' | 'requests'>('all');

  const { data, loading, error, refetch } = useQuery(GET_QUOTES, {
    variables: { agencyId: Number(agencyId) },
    skip: !agencyId,
    fetchPolicy: 'cache-and-network',
  });

  const [deleteQuote, { loading: deleting }] = useMutation(DELETE_QUOTE, {
    onCompleted: () => refetch(),
  });
  // (envoi multi-canal géré dans la page détail)
  const [duplicateQuote, { loading: duplicating }] = useMutation(DUPLICATE_QUOTE, {
    onCompleted: () => refetch(),
  });

  const quotes: QuoteRow[] = data?.quotes || [];

  const pendingRequestsCount = useMemo(
    () =>
      quotes.filter(
        (q) => q.source === 'CLIENT' && (q.status === 'DRAFT' || q.status === 'SENT')
      ).length,
    [quotes]
  );

  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    return quotes.filter((q) => {
      if (tab === 'requests' && q.source !== 'CLIENT') return false;
      const matchStatus = filterStatus === 'all' || q.status === filterStatus;
      if (!matchStatus) return false;
      if (!term) return true;
      return (
        q.reference.toLowerCase().includes(term) ||
        q.title.toLowerCase().includes(term) ||
        `${q.contactPrenom} ${q.contactNom}`.toLowerCase().includes(term) ||
        (q.contactEmail || '').toLowerCase().includes(term) ||
        (q.destination || '').toLowerCase().includes(term)
      );
    });
  }, [quotes, searchTerm, filterStatus, tab]);

  const handleDelete = async (id: number) => {
    if (!confirm('Supprimer ce devis ? Cette action est irréversible.')) return;
    try {
      await deleteQuote({ variables: { id } });
    } catch (e: any) {
      alert('Erreur : ' + e.message);
    }
  };

  const handleSend = (id: number) => {
    navigate(`/admin/quotes/${id}`);
  };

  const handleDuplicate = async (id: number) => {
    try {
      const res = await duplicateQuote({ variables: { id } });
      const newId = res.data?.duplicateQuote?.id;
      if (newId) navigate(`/admin/quotes/${newId}/edit`);
    } catch (e: any) {
      alert('Erreur : ' + e.message);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold">Devis</h1>
            <p className="text-sm text-gray-500">
              Créez, suivez et convertissez vos devis voyage en réservations.
            </p>
          </div>
          <button
            onClick={() => navigate('/admin/quotes/new')}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium"
          >
            <Plus className="w-4 h-4" />
            Nouveau devis
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-gray-200">
          <TabBtn active={tab === 'all'} onClick={() => setTab('all')}>
            Tous les devis
          </TabBtn>
          <TabBtn
            active={tab === 'requests'}
            onClick={() => setTab('requests')}
            badge={pendingRequestsCount > 0 ? pendingRequestsCount : undefined}
          >
            Demandes clients
          </TabBtn>
        </div>

        {/* Filtres */}
        <div className="flex gap-4 bg-white p-4 rounded-lg shadow flex-wrap">
          <div className="flex-1 relative min-w-[200px]">
            <input
              type="text"
              placeholder="Rechercher (référence, titre, client, email...)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
          </div>
          <div className="relative">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md appearance-none bg-white"
            >
              <option value="all">Tous les statuts</option>
              {(Object.entries(statusLabels) as [QuoteStatus, string][]).map(
                ([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                )
              )}
            </select>
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
          </div>
        </div>

        {/* Liste */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {loading && !data ? (
            <div className="p-8 text-center text-gray-500">Chargement des devis...</div>
          ) : error ? (
            <div className="p-8 text-center text-red-600">
              Erreur : {error.message}
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500 mb-4">
                {quotes.length === 0
                  ? "Aucun devis pour l'instant."
                  : 'Aucun devis ne correspond aux filtres.'}
              </p>
              {quotes.length === 0 && (
                <button
                  onClick={() => navigate('/admin/quotes/new')}
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
                >
                  <Plus className="w-4 h-4" /> Créer le premier devis
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <Th>Référence</Th>
                    <Th>Client</Th>
                    <Th>Destination / Titre</Th>
                    <Th>Voyage</Th>
                    <Th>Validité</Th>
                    <Th>Montant</Th>
                    <Th>Statut</Th>
                    <Th>Actions</Th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filtered.map((q) => (
                    <tr key={q.id} className="hover:bg-gray-50">
                      <td className="px-6 py-3 whitespace-nowrap">
                        <span className="font-mono text-sm text-gray-900">
                          {q.reference}
                        </span>
                      </td>
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-medium text-gray-900">
                            {q.contactPrenom} {q.contactNom}
                          </div>
                          {q.source === 'CLIENT' && (
                            <span
                              className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase bg-amber-100 text-amber-800"
                              title="Demande venue depuis le site agence"
                            >
                              Demande client
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">{q.contactEmail}</div>
                      </td>
                      <td className="px-6 py-3">
                        <div className="text-sm text-gray-900">{q.title}</div>
                        {q.destination && (
                          <div className="text-xs text-gray-500">{q.destination}</div>
                        )}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700">
                        {formatDate(q.travelDateStart)}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700">
                        {formatDate(q.validUntil)}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm font-semibold text-gray-900">
                        {formatCurrency(q.totalPrice, q.currency)}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            statusColors[q.status] || 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {statusLabels[q.status] || q.status}
                        </span>
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => navigate(`/admin/quotes/${q.id}`)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Voir"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                          {(q.status === 'DRAFT' || q.status === 'SENT') && (
                            <button
                              onClick={() => handleSend(q.id)}
                              className="text-green-600 hover:text-green-900"
                              title="Envoyer (email/WhatsApp)"
                            >
                              <Send className="w-5 h-5" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDuplicate(q.id)}
                            disabled={duplicating}
                            className="text-gray-600 hover:text-gray-900 disabled:opacity-50"
                            title="Dupliquer"
                          >
                            <Copy className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(q.id)}
                            disabled={deleting}
                            className="text-red-600 hover:text-red-900 disabled:opacity-50"
                            title="Supprimer"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      {children}
    </th>
  );
}

function TabBtn({
  active,
  onClick,
  badge,
  children,
}: {
  active: boolean;
  onClick: () => void;
  badge?: number;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 -mb-px border-b-2 text-sm font-medium ${
        active
          ? 'border-blue-600 text-blue-600'
          : 'border-transparent text-gray-500 hover:text-gray-700'
      }`}
    >
      {children}
      {badge !== undefined && (
        <span className="ml-2 inline-flex items-center justify-center min-w-[20px] px-1.5 h-5 rounded-full bg-amber-500 text-white text-xs font-semibold">
          {badge}
        </span>
      )}
    </button>
  );
}
